import React from 'react';
import { v4 } from 'uuid';
import { WORKFLOW_ARTIFACT_TYPE } from '@cotera/api';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { Button } from '@cotera/client/v0/components/ui/button';
import { toast } from '@cotera/client/app/components/ui';
import type { WorkflowSeedNewDatasetTrigger } from '../workflow/workflow.model';
import { Icon } from '../../components/ui/icon';

/**
 * Opens a brand-new, unsaved workflow in the artifact panel, pre-bound to a
 * dataset trigger watching THIS dataset.
 *
 * The workflow is not persisted — it opens as a draft the user builds out and
 * saves (or discards) themselves. The seed rides in the artifact payload rather
 * than a URL, which is what keeps the dataset open in the same panel: the
 * workflow lands as a sibling tab instead of navigating away to a fresh builder
 * chat. The seeded trigger lives only in the draft (not its baseline), so the
 * dataset trigger is created the first time the user saves.
 */
/**
 * Whether an automation can be added from the current surface at all.
 *
 * Adding one opens a draft workflow as a sibling tab in the artifact panel, so
 * a dataset opened as a standalone page — its own route, no panel — has nowhere
 * to put it. Exported so a surface can say that up front instead of drawing a
 * control whose only outcome is an error toast.
 */
export const useCanAddAutomation = (): boolean => useArtifactPanel() !== null;

export const DatasetAddAutomationButton: React.FC<{
  datasetId: string;
  datasetName: string;
  /** Fired once the draft is on its way, so a menu holding this can close. */
  onAdded?: () => void;
  /**
   * How much of the eye to ask for.
   *
   * `button` is the filled call to action, for the places where binding the
   * first automation is the whole point of the surface. `row` is a quiet line
   * that sits at the end of a list of automations that already exist, where a
   * filled button would compete with the control that dispatches the runs.
   */
  variant?: 'button' | 'row';
}> = ({ datasetId, datasetName, onAdded, variant = 'button' }) => {
  const artifactPanel = useArtifactPanel();

  const addAutomation = React.useCallback(() => {
    // The button only renders inside the artifact panel, but a dataset opened as
    // a standalone page has no panel to add a tab to.
    if (artifactPanel === null) {
      toast.error('Open this dataset in a chat to add an automation');
      return;
    }
    const workflowId = v4();
    const seedNewDatasetTrigger: WorkflowSeedNewDatasetTrigger = {
      datasetId,
      // Named after the dataset so it reads sensibly in the trigger list; the
      // builder lets the user rename it.
      name: datasetName,
      // The friendliest default — fire once per new row. The builder exposes the
      // other criteria and the automatic/manual switch.
      criterion: 'new_row',
      runMode: 'automatic',
    };
    artifactPanel.openArtifact({
      type: WORKFLOW_ARTIFACT_TYPE,
      artifactId: workflowId,
      payload: { workflowId, seedNewDatasetTrigger },
    });
    onAdded?.();
  }, [artifactPanel, datasetId, datasetName, onAdded]);

  // Rendered nowhere the click could not land. The empty state substitutes a
  // line saying where to open the dataset instead; the row variant is a
  // secondary affordance on a list that still reads fine without it.
  if (artifactPanel === null) {
    return null;
  }

  if (variant === 'row') {
    return (
      <button
        type="button"
        className="mt-1 flex w-full cursor-pointer items-center gap-2.5 rounded px-1 py-1.5 text-left text-[11px] text-muted-foreground hover:bg-muted/60 hover:text-foreground"
        onClick={addAutomation}
      >
        {/* Indented to the width of the checkbox column above it, so the label
            starts where the workflow names do rather than under their marks. */}
        <span className="flex size-4 shrink-0 items-center justify-center">
          <Icon icon="plus" size="small" />
        </span>
        Add automation
      </button>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="default"
      className="h-7 gap-1 px-2.5"
      onClick={addAutomation}
    >
      <Icon icon="plus" />
      Add automation
    </Button>
  );
};
